import React from 'react'
import MaterialTable, { MaterialTableProps, Query, Column } from 'material-table'
import { Tooltip, makeStyles } from '@material-ui/core'

const useStyles = makeStyles({
  tooltip: {
    fontSize: '16px',
    fontFamily: 'Montserrat',
  },
  tableWrapper: {
    '& .MuiTableCell-head': {
      fontFamily: 'Montserrat',
      fontWeight: 700,
      fontSize: '16px',
      lineHeight: '20px',
    },
    '& .MuiTableCell-body': {
      fontFamily: 'Montserrat',
      fontWeight: 400,
      fontSize: '14px',
    },
    '& .MuiInputBase-input': {
      fontFamily: 'Montserrat',
      fontWeight: 400,
      fontSize: '14px',
    },
    '& .MuiTypography-body2': {
      fontFamily: 'Montserrat',
      fontWeight: 400,
      fontSize: '14px',
    },
    '& .MuiButton-root': {
      fontFamily: 'Montserrat',
      fontWeight: 400,
      fontSize: '14px',
    },
    '& .MuiSelect-root': {
      fontFamily: 'Montserrat',
      fontWeight: 400,
      fontSize: '14px',
    },
    // Estilos para el título de la tabla (Material-Table toolbar)
    '& .MuiToolbar-root h6': {
      fontFamily: 'Montserrat',
      fontWeight: 700,
      fontSize: '16px',
      lineHeight: '20px',
    },
    '& .MuiTypography-h6': {
      fontFamily: 'Montserrat',
      fontWeight: 700,
      fontSize: '16px',
      lineHeight: '20px',
    },
    '& .MuiTypography-root': {
      fontFamily: 'Montserrat',
    },
  },
})

const removeAccents = (value: unknown): string =>
  typeof value === 'string'
    ? value.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    : String(value ?? '')

const buildPaginationQuery = <T extends object>(query: Query<T>) => {
  const { page, pageSize, search, orderBy, orderDirection } = query

  const pageQuery = `page=${page ?? 0}`
  const pageSizeQuery = `&pageSize=${pageSize ?? 10}`
  const searchQuery = search ? `&search=${search}` : ''
  const sortField = orderBy?.field ? String(orderBy.field) : ''
  const orderByQuery = sortField ? `&orderBy=${sortField}` : ''
  const orderDirectionQuery = sortField && orderDirection ? `&orderDirection=${orderDirection}` : ''

  return `${pageQuery}${pageSizeQuery}${searchQuery}${orderByQuery}${orderDirectionQuery}`
}

const isSortableColumn = <T extends object>(column: Column<T>) =>
  column.sorting !== false && (Boolean(column.field) || Boolean(column.customSort))

const DefaultTable = <T extends object>(props: MaterialTableProps<T>) => {
  const classes = useStyles()
  const { options, columns, ...restProps } = props

  const validColumns = columns.filter((column): column is Column<T> => Boolean(column?.title))

  const enhancedColumns = validColumns.map((column: Column<T>, index: number) => {
    const enhanced: Column<T> = {
      ...column,
      ...(isSortableColumn(column) ? {} : { sorting: false }),
      cellStyle: {
        ...column.cellStyle,
        maxWidth: '200px',
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden' as const,
        textOverflow: 'ellipsis' as const,
        paddingLeft: index === 0 ? '24px' : undefined,
        fontFamily: 'Montserrat',
        fontWeight: 400,
        fontSize: '14px',
      },
      headerStyle: {
        ...column.headerStyle,
        paddingLeft: index === 0 ? '24px' : undefined,
        fontFamily: 'Montserrat',
        fontWeight: 700,
        fontSize: '14px',
        lineHeight: '18px',
      },
      render:
        column.render ??
        ((rowData: T) => {
          const fieldKey = column.field as keyof T
          const cellValue = fieldKey ? rowData[fieldKey] : undefined
          return cellValue ? (
            <Tooltip
              title={String(cellValue)}
              placement="bottom-start"
              classes={{ tooltip: classes.tooltip }}
            >
              <div
                style={{
                  whiteSpace: 'nowrap' as const,
                  overflow: 'hidden' as const,
                  textOverflow: 'ellipsis' as const,
                }}
              >
                {cellValue}
              </div>
            </Tooltip>
          ) : null
        }),
    }

    // Provide default accent-insensitive search if not explicitly defined
    if (enhanced.field && !enhanced.customFilterAndSearch) {
      enhanced.customFilterAndSearch = (term: string, rowData: T) => {
        const value = rowData[enhanced.field as keyof T]
        if (value === null || value === undefined) return false
        const normalizedValue = removeAccents(value).toLowerCase()
        const normalizedTerm = removeAccents(term).toLowerCase()
        return normalizedValue.indexOf(normalizedTerm) !== -1
      }
    }

    return enhanced
  })

  return (
    <div className={classes.tableWrapper} style={{ maxWidth: '100%' }}>
      <MaterialTable
        localization={{
          pagination: {
            labelDisplayedRows: '{from}-{to} de {count}',
            labelRowsSelect: 'filas',
          },
          toolbar: {
            nRowsSelected: '{0} filas(s) seleccionadas',
            searchTooltip: 'Buscar',
            searchPlaceholder: 'Buscar',
          },
          header: {
            actions: 'Acciones',
          },
          body: {
            emptyDataSourceMessage: 'No hay nada todavía.',
            filterRow: {
              filterTooltip: 'Filtrar',
            },
          },
        }}
        options={{
          pageSize: 10,
          pageSizeOptions: [5, 10, 20, 50, 100],
          actionsColumnIndex: validColumns.length,
          emptyRowsWhenPaging: false,
          searchFieldStyle: {
            marginRight: '8px',
            fontFamily: 'Montserrat',
            fontWeight: 400,
            fontSize: '14px',
          },
          headerStyle: {
            padding: '16px',
            fontFamily: 'Montserrat',
            fontWeight: 700,
            fontSize: '16px',
            lineHeight: '20px',
          },
          rowStyle: (data, index) => ({
            backgroundColor: index % 2 === 0 ? '#FAFAFA' : '#FFF',
          }),
          actionsCellStyle: {
            paddingRight: '8px',
          },
          ...options,
        }}
        columns={enhancedColumns}
        {...restProps}
      />
    </div>
  )
}

export { DefaultTable, buildPaginationQuery }
